// Daughter-cell checkpoints (bead monorail-s8k): small budded-off cells of
// the Mother, planted at biome-band entrances. Swim into one once to attune
// (free, last-touched wins, edge-latched); on death you respawn there
// instead of at the origin hub. The death-spot DNA-orb drop is untouched —
// checkpoints only cut travel, they never soften the souls-tension of
// retrieval.
//
// Module-scope attunement state survives world.clear() the same way
// mother.ts's rest timer does — respawn reseeds the world, not this module.

import type { Entity, Vec2 } from '../components'
import type { GameWorld } from '../world'
import type { EventBus } from '../events'
import { emitRipple, spawnPigmentPuff } from './particles'
import { playSfx } from '../../engine/audio'
import { T } from '../tunables'

let attunedId: string | null = null

/** Restore a persisted attunement (main.ts, before seedDemo on load). */
export function restoreAttunement(id: string | null): void {
  attunedId = id
}

export function attunedCheckpointId(): string | null {
  return attunedId
}

/** The attuned cell's current world position, or null when unattuned or the
 *  persisted id no longer resolves (a reroll changed the world) — callers
 *  fall back to the origin hub, never crash on a stale id. */
export function attunedCheckpointPos(world: GameWorld): Vec2 | null {
  if (attunedId === null) return null
  for (const cp of world.with('checkpoint', 'position')) {
    if (cp.checkpoint.id === attunedId) return { x: cp.position.x, y: cp.position.y }
  }
  return null
}

// Warm gold-rose highlight the attuned cell blends toward — a clearly-visible
// shift (not a subtle one; watercolor noise swallows subtle tweaks).
const ATTUNED_HIGHLIGHT: [number, number, number] = [1.0, 0.85, 0.55]
const ATTUNED_BLEND = 0.4

function attunedTint(base: readonly [number, number, number]): [number, number, number] {
  return [
    base[0] + (ATTUNED_HIGHLIGHT[0] - base[0]) * ATTUNED_BLEND,
    base[1] + (ATTUNED_HIGHLIGHT[1] - base[1]) * ATTUNED_BLEND,
    base[2] + (ATTUNED_HIGHLIGHT[2] - base[2]) * ATTUNED_BLEND,
  ]
}

// Per-instance tint is a live render field (checkpoint.attuned drives it),
// so the original spawn tint is cached here rather than added to the
// component — components.ts's checkpoint shape stays exactly {id, attuned}.
const baseTint = new WeakMap<Entity, [number, number, number]>()

function attuneFlourish(world: GameWorld, at: Vec2): void {
  emitRipple(at.x, at.y, { strength: 0.3, lifetime: 0.7 })
  for (let i = 0; i < 6; i++) {
    const a = (i / 6) * Math.PI * 2
    spawnPigmentPuff(world, at.x + Math.cos(a) * 0.12, at.y + Math.sin(a) * 0.12, ATTUNED_HIGHLIGHT, 0.045)
  }
  playSfx('shield_up', { volume: 0.3 })
}

export function checkpointSystem(world: GameWorld, player: Entity, dt: number, events: EventBus): void {
  const cells = [...world.with('checkpoint', 'position', 'renderable')]
  if (cells.length === 0) return

  const c = T.checkpoint
  const pr = player.renderable?.radius ?? T.player.baseRadius

  // ── Overlap edge-latch (mirrors portal.ts) ───────────────────────────────
  let overlapping: Entity | null = null
  for (const cp of cells) {
    const dx = cp.position.x - player.position.x
    const dy = cp.position.y - player.position.y
    const reach = pr + cp.renderable.radius * c.attuneReachFrac
    if (dx * dx + dy * dy < reach * reach) {
      overlapping = cp
      break
    }
  }
  if (overlapping && !player.overlapCheckpoint && overlapping.checkpoint!.id !== attunedId) {
    attunedId = overlapping.checkpoint!.id
    for (const cp of cells) cp.checkpoint!.attuned = cp.checkpoint!.id === attunedId
    events.emit({ type: 'checkpoint.attuned', id: attunedId, x: overlapping.position.x, y: overlapping.position.y })
    attuneFlourish(world, overlapping.position)
  }
  player.overlapCheckpoint = overlapping !== null

  // ── Persistent attuned visual ────────────────────────────────────────────
  for (const cp of cells) {
    if (!baseTint.has(cp)) baseTint.set(cp, [...cp.renderable.tint] as [number, number, number])
    const base = baseTint.get(cp)!
    cp.renderable.tint = cp.checkpoint!.attuned ? attunedTint(base) : base
  }

  // ── Heal aura — attuned cell only; healing has no rest/sleep mechanic
  // (that's the Mother's signature, not hers to borrow). ───────────────────
  if (!player.health) return
  const attuned = cells.find(cp => cp.checkpoint!.attuned)
  if (!attuned) return
  const dx = player.position.x - attuned.position.x
  const dy = player.position.y - attuned.position.y
  if (dx * dx + dy * dy > c.auraRadius * c.auraRadius) return
  player.health.current = Math.min(player.health.max, player.health.current + c.auraRegen * dt)
}
